import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, CircleHelp, ExternalLink, Eye, RefreshCw, Stethoscope, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, type ShopHealthCheck } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { t, tn } from "@/lib/i18n";

const ICON: Record<ShopHealthCheck["status"], { icon: typeof CheckCircle2; cls: string }> = {
  ok: { icon: CheckCircle2, cls: "text-success" },
  warn: { icon: AlertTriangle, cls: "text-warning" },
  error: { icon: XCircle, cls: "text-danger" },
  manual: { icon: Eye, cls: "text-info" },
  unknown: { icon: CircleHelp, cls: "text-fg-subtle" },
};
const ORDER: ShopHealthCheck["status"][] = ["error", "warn", "manual", "unknown", "ok"];

/**
 * Shop-Check: prüft live im Shop, ob alles für den Betrieb stimmt (Standort, Versandprofile, Berechtigungen,
 * Webhooks …). Probleme stehen oben, mit Ein-Klick-Lösung oder Link an die richtige Stelle in Shopify.
 */
export function ShopHealthCard({ channelId, editable }: { channelId: string; editable: boolean }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shop-health", tenantId, channelId], queryFn: () => api.channels.shopHealth(tenantId, channelId), staleTime: 5 * 60_000, retry: false });
  const [showOk, setShowOk] = useState(false);
  const fix = useMutation({
    mutationFn: (f: NonNullable<ShopHealthCheck["fix"]>) => api.channels.shopHealthFix(tenantId, channelId, f),
    onSuccess: (r) => {
      toast.success(r.message);
      // Shopify braucht einen Moment – danach neu prüfen
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["shop-health", tenantId, channelId] }), 2500);
    },
    onError: (e) => toast.error(t("Nicht behoben"), { description: e instanceof ApiError ? (e.body as { message?: string })?.message : undefined }),
  });

  const checks = [...(q.data?.checks ?? [])].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));
  const open = checks.filter((c) => c.status !== "ok");
  const ok = checks.filter((c) => c.status === "ok");
  const worst = open[0]?.status;

  return (
    <Card className={cx("overflow-hidden", worst === "error" && "border-danger/30", worst === "warn" && "border-warning/30")}>
      <div className="px-5 py-3.5 flex flex-wrap items-center gap-3 border-b border-border">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><Stethoscope className="size-4 text-fg-muted" /> {t("Shop-Check")}</h2>
          <p className="text-[12.5px] text-fg-muted">
            {q.isLoading ? t("Prüft den Shop …") : q.isError ? t("Prüfung nicht möglich") : open.length ? tn(open.length, "{n} Punkt braucht Aufmerksamkeit", "{n} Punkte brauchen Aufmerksamkeit") : t("Alles in Ordnung – Aufträge, Bestand und Versand laufen reibungslos.")}
            {q.data && <span className="text-fg-subtle"> · {t("geprüft")} {fmtRelative(q.data.checkedAt)}</span>}
          </p>
        </div>
        <Button size="sm" icon={<RefreshCw className={cx("size-3.5", q.isFetching && "animate-spin")} />} disabled={q.isFetching} onClick={() => void q.refetch()}>{t("Erneut prüfen")}</Button>
      </div>
      {q.isLoading && <div className="p-4 space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>}
      {q.isError && <p className="px-5 py-3 text-[13px] text-danger">{q.error instanceof ApiError ? ((q.error.body as { message?: string })?.message ?? t("Shopify nicht erreichbar.")) : t("Shopify nicht erreichbar.")}</p>}
      {open.length > 0 && (
        <ul className="divide-y divide-border">
          {open.map((c) => <Row key={c.key} c={c} editable={editable} pending={fix.isPending && fix.variables === c.fix} onFix={(f) => fix.mutate(f)} />)}
        </ul>
      )}
      {ok.length > 0 && (
        <div className={cx(open.length > 0 && "border-t border-border")}>
          <button type="button" onClick={() => setShowOk(!showOk)} className="w-full flex items-center gap-2 px-5 py-2.5 text-[12.5px] text-fg-muted hover:bg-surface-2/60">
            <CheckCircle2 className="size-3.5 text-success" /> {tn(ok.length, "{n} Prüfung in Ordnung", "{n} Prüfungen in Ordnung")}
            <ChevronDown className={cx("ml-auto size-3.5 transition-transform", showOk && "rotate-180")} />
          </button>
          {showOk && <ul className="divide-y divide-border border-t border-border">{ok.map((c) => <Row key={c.key} c={c} editable={false} pending={false} onFix={() => {}} />)}</ul>}
        </div>
      )}
    </Card>
  );
}

function Row({ c, editable, pending, onFix }: { c: ShopHealthCheck; editable: boolean; pending: boolean; onFix: (f: NonNullable<ShopHealthCheck["fix"]>) => void }) {
  const I = ICON[c.status];
  return (
    <li className="px-5 py-3 flex items-start gap-3">
      <I.icon className={cx("size-4 mt-0.5 shrink-0", I.cls)} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium">{t(c.title)}</p>
        <p className="text-[12.5px] text-fg-muted mt-0.5">{t(c.detail)}</p>
        {(c.link || (c.fix && editable)) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {c.fix && editable && <Button size="sm" variant="primary" loading={pending} onClick={() => onFix(c.fix!)}>{c.fixLabel ?? t("Beheben")}</Button>}
            {c.link && <a href={c.link.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-fg-muted hover:text-fg underline-offset-2 hover:underline"><ExternalLink className="size-3.5" /> {t(c.link.label)}</a>}
          </div>
        )}
      </div>
    </li>
  );
}
